// src/pages/MainPage.js
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import KakaoAdFit from "../components/KakaoAdFit"; // ✅ 광고 컴포넌트 임포트
import SupportPopup from "../components/SupportPopup";
import SupportSection from "../components/SupportSection";
import "./MainPage.css";

function MainPage() {
  const location = useLocation();
  const pathname = location?.pathname || "/";
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const {
    currentLocale,
    prefix,
    canonicalUrl,
    htmlLang,
    ogLocale,
    inLanguage,
    altPaths,
  } = useMemo(() => {
    const firstSeg = pathname.split("/").filter(Boolean)[0];
    const supported = ["en", "ja", "tw"];
    const locale = supported.includes(firstSeg) ? firstSeg : "ko";

    const pref = locale === "ko" ? "" : `/${locale}`;

    const segments = pathname.split("/").filter(Boolean);
    const suffixSegments = supported.includes(segments[0])
      ? segments.slice(1)
      : segments;
    const suffixPath = "/" + suffixSegments.join("/");
    const normalizedSuffix = suffixPath === "//" ? "/" : suffixPath;

    const alt = {
      ko: normalizedSuffix,
      en: "/en" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
      ja: "/ja" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
      tw: "/tw" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
    };

    const htmlLangMap = { ko: "ko", en: "en", ja: "ja", tw: "zh-TW" };
    const ogLocaleMap = { ko: "ko_KR", en: "en_US", ja: "ja_JP", tw: "zh_TW" };
    const inLangMap = { ko: "ko-KR", en: "en", ja: "ja", tw: "zh-TW" };

    return {
      currentLocale: locale,
      prefix: pref,
      canonicalUrl: (origin || "") + pathname,
      htmlLang: htmlLangMap[locale],
      ogLocale: ogLocaleMap[locale],
      inLanguage: inLangMap[locale],
      altPaths: alt,
    };
  }, [pathname, origin]);

  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "TooLoves",
      description: "자주 쓰는 도구를 한곳에서 빠르게",
      inLanguage,
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl, inLanguage, origin]);

  const alternateLinks =
    origin &&
    [
      <link key="ko" rel="alternate" hrefLang="ko" href={origin + altPaths.ko} />,
      <link key="en" rel="alternate" hrefLang="en" href={origin + altPaths.en} />,
      <link key="ja" rel="alternate" hrefLang="ja" href={origin + altPaths.ja} />,
      <link key="tw" rel="alternate" hrefLang="zh-TW" href={origin + altPaths.tw} />,
      <link key="xdef" rel="alternate" hrefLang="x-default" href={origin + altPaths.ko} />,
    ];

  return (
    <div className="main-wrap">
      <SupportPopup locale="ko" />
      <Helmet htmlAttributes={{ lang: htmlLang }}>
        <title>TooLoves</title>
        <meta name="description" content="자주 쓰는 도구를 한곳에서 빠르게" />
        <meta
          name="keywords"
          content="TooLoves, 도구 모음,QR, QR 코드 생성, Base64,워터마크,워터마크 생성, EXIF, 메타데이터 추출, AI 이미지, 무료, 웹 유틸,블로그,웹 개발"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#ffffff" />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="TooLoves" />
        <meta
          property="og:description"
          content="자주 쓰는 도구를 한곳에서 빠르게"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-main.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TooLoves" />
        <meta
          name="twitter:description"
          content="자주 쓰는 도구를 한곳에서 빠르게"
        />
        <meta name="twitter:image" content="/og-main.png" />

        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {alternateLinks}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLd }}
        />

        <style>
          {`
            .lang-switcher {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
            }
            .lang-switcher .lang-btn {
              color: inherit;
              opacity: 0.7;
              text-decoration: none;
              padding: 4px 8px;
              border-radius: 8px;
              transition: opacity .15s, background-color .15s;
            }
            .lang-switcher .lang-btn:hover {
              opacity: 1;
              background: rgba(0,0,0,0.06);
            }
            .lang-switcher .lang-btn.active {
              opacity: 1;
              font-weight: 600;
              background: rgba(0,0,0,0.08);
            }
            .lang-switcher .lang-sep {
              opacity: 0.4;
              user-select: none;
            }
            @media (prefers-color-scheme: dark) {
              .lang-switcher .lang-btn:hover {
                background: rgba(255,255,255,0.08);
              }
              .lang-switcher .lang-btn.active {
                background: rgba(255,255,255,0.12);
              }
            }
          `}
        </style>
      </Helmet>

      <header className="main-hero">
        <nav className="main-nav">
          <div className="nav-left"></div>
          <div className="nav-right">
            <div className="lang-switcher" role="group" aria-label="언어 선택">
              {["ko", "en", "ja", "tw"].map((lang, i, arr) => (
                <React.Fragment key={lang}>
                  <Link
                    to={altPaths[lang]}
                    className={`lang-btn ${
                      currentLocale === lang ? "active" : ""
                    }`}
                    lang={lang === "tw" ? "zh-TW" : lang}
                    aria-current={
                      currentLocale === lang ? "page" : undefined
                    }
                  >
                    {lang === "ko"
                      ? "한국어"
                      : lang === "en"
                      ? "English"
                      : lang === "ja"
                      ? "日本語"
                      : "繁體中文"}
                  </Link>
                  {i < arr.length - 1 && (
                    <span className="lang-sep" aria-hidden="true">
                      /
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </nav>

        <div className="hero-content">
          <h1 className="hero-title">TooLoves</h1>
          <p className="hero-subtitle">자주 쓰는 도구를 한곳에서 빠르게</p>
        </div>
      </header>

      <main className="services">
        <SupportSection locale="ko" />
        <h2 className="section-title">서비스 모음</h2>
        
        {/* ✅ 광고 추가 (서비스 목록 바로 위) */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px' }}>
          <KakaoAdFit />
        </div>

        <div className="service-grid">
          {/* QR 코드 생성기 */}
          <article className="service-card">
            <div className="service-icon service-icon-qr" aria-hidden="true">
              <svg viewBox="0 0 64 64" width="48" height="48" role="img">
                <rect x="6" y="6" width="18" height="18" rx="2" />
                <rect x="40" y="6" width="18" height="18" rx="2" />
                <rect x="6" y="40" width="18" height="18" rx="2" />
                <rect x="26" y="26" width="12" height="12" />
              </svg>
            </div>
            <h3 className="service-name">QR 코드</h3>
            <p className="service-desc">웹 주소를 QR로 만들고 이미지로 저장</p>
            <Link to={`${prefix}/qr`} className="btn-primary">
              QR 생성기로 이동
            </Link>
          </article>

          {/* 이미지 워터마크 */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🖼️</div>
            <h3 className="service-name">이미지 워터마크 추가하기</h3>
            <p className="service-desc">이미지에 나만의 워터마크를 만드세요</p>
            <Link to={`${prefix}/watermark`} className="btn-primary">
              워터마크 생성기로 이동
            </Link>
          </article>

          {/* AI사진 EXIF */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🌀</div>
            <h3 className="service-name">NovelAI사진 EXIF 추출기</h3>
            <p className="service-desc">NovelAI의 메타데이터를 간편하게</p>
            <Link to={`${prefix}/aiexif`} className="btn-primary">
              EXIF추출기로 이동
            </Link>
          </article>

          {/* Base64 인코더/디코더 */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🔤</div>
            <h3 className="service-name">Base64 인코더/디코더</h3>
            <p className="service-desc">텍스트를 Base64로 인코딩 or 디코딩</p>
            <Link to={`${prefix}/base64`} className="btn-primary">
              Base64 변환기로 이동
            </Link>
          </article>
        </div>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. All rights reserved.</span>
        <nav className="footer-links" aria-label="법적 고지 링크">
          <Link to={`${prefix}/terms`} className="footer-link">
            이용약관
          </Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to={`${prefix}/privacy`} className="footer-link">
            개인정보처리방침
          </Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <a
            href="https://tooloves.net/blog"
            className="footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            블로그
          </a>
        </nav>
      </footer>
    </div>
  );
}

export default MainPage;
